import { useEffect, useRef, useState, useCallback } from "react";
import VideoCard from "../components/common/VideoCard";
import useSelectVideo from "../hooks/useSelectVideo";
import { IoMdArrowBack } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { getHashtag, resetHashtagState } from "../redux/ApiSlice/HashTagSlice";
import TitleSection from "../components/hashTag/TitleSection";
import SkeletonVideoCard from "../components/skeleton/SkeletonVideoCard";

export default function HashTagPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const slelectVideo = useSelectVideo();
    const [searchText, setSearchText] = useState("");
    const scrollContainerRef = useRef(null);

    const hashTag = useSelector((state) => state.HashTag);
    const { hashTagList, loading, hasMore, loadingMore, currentPage } = hashTag;

    // Extract videos from the results array
    const videos = hashTagList?.results || [];
    const totalCount = hashTagList?.count || 0;
    let hash = window.location.hash;
    hash = hash.replace("#", "");

    const handleSelectVideo = (id) => {
        slelectVideo(id, videos, `${location.pathname}${location.hash}`);
    };

    const filteredVideos = searchText.length !== "" ? videos?.filter((video) =>
        video?.post_description?.toLowerCase().includes(searchText?.toLowerCase())
    ) : videos;

    const handleChange = (e) => {
        setSearchText(() => e.target.value);
    };

    // Load more videos when scrolling to bottom
    const loadMoreVideos = useCallback(async () => {
        // Check if we should load more based on next URL availability
        if (loadingMore || !hasMore || !hashTagList?.next) return;

        try {
            const nextPage = currentPage + 1;
            await dispatch(getHashtag({
                hashtag: hash,
                page: nextPage,
                append: true
            }));
        } catch (error) {
            console.error("Error loading more videos:", error);
        }
    }, [dispatch, hash, currentPage, hasMore, loadingMore, hashTagList?.next]);

    // Handle scroll event for infinite loading
    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold

        // Only load more if we have a next URL
        if (isNearBottom && !loadingMore && hasMore && hashTagList?.next) {
            loadMoreVideos();
        }
    }, [loadMoreVideos, loadingMore, hasMore, hashTagList?.next]);

    useEffect(() => {
        dispatch(getHashtag({ hashtag: hash, page: 1 }));

        // Cleanup function to reset state when hashtag changes
        return () => {
            if (hash !== window.location.hash.replace("#", "")) {
                dispatch(resetHashtagState());
            }
        };
    }, [dispatch, hash]);

    // Handle back navigation
    const handleBack = () => {
        if (location.state && location.state.from === "search") {
            navigate("/search", { state: { activeTab: "Hashtag", from: "hashtag" } });
        } else {
            navigate("/");
        }
    };

    return (
        <section className="h-full bg-white dark:bg-black relative">
            <div className="absolute left-2 top-4 z-50 flex items-center justify-center text-[#fff]">
                <IoMdArrowBack
                    onClick={handleBack}
                    className="text-black dark:text-white text-[18px] cursor-pointer"
                />
            </div>
            <TitleSection
                hash={hash}
                inputvalue={searchText}
                handleChange={handleChange}
            />
            {loading && videos.length === 0 ?
                <SkeletonVideoCard /> :
                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className="absolute h-[-webkit-fill-available] overflow-auto no-scrollbar mt-1 w-full"
                >
                    <div className="flex flex-wrap overflow-scroll no-scrollbar">
                        <div className="grid grid-cols-3 p-2 gap-1 w-full overflow-y-scroll">
                            {filteredVideos?.length > 0 && filteredVideos?.map((item, index) => (
                                <VideoCard
                                    key={`${item.id}-${index}`}
                                    id={item.id}
                                    threeDots={false}
                                    view_count={item.view_count}
                                    video={item.video_url}
                                    body={item.post_description}
                                    likes_count={item.likes_count}
                                    authorId={item.created_by?.id}
                                    selectVideo={handleSelectVideo}
                                    avatar_url={item.created_by?.avatar_url}
                                />
                            ))}

                            {/* Loading more indicator */}
                            {loadingMore && (
                                <div className="col-span-3 flex justify-center items-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                                    {/* <span className="ml-2 text-gray-600 dark:text-gray-300">Loading more...</span> */}
                                </div>
                            )}

                            {/* No videos message */}
                            {!loading && filteredVideos?.length === 0 && (
                                <div className="col-span-3 text-center text-gray-500 text-[12px] py-8">
                                    {searchText ? `No videos found for "${searchText}"` : "No videos found for this hashtag."}
                                </div>
                            )}

                            {/* End of results indicator */}
                            {/* {!loading && !loadingMore && videos.length > 0 && (!hasMore || !hashTagList?.next) && (
                                <div className="col-span-3 text-center text-gray-400 text-[10px] py-4">
                                    {totalCount > 0 ? `All ${totalCount} videos loaded` : "End of results"}
                                </div>
                            )} */}
                        </div>
                    </div>
                </div>
            }
        </section>
    );
}
